import { Badge } from '@/components/ui/badge';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
	Globe,
	Mail,
	MapPin,
	MessageCircle,
	Phone,
	Smartphone,
	User
} from 'lucide-react';

interface CustomerContactDisplayItemProps {
	label?: string | null;
	iconHint?: string | null;
	value?: string | null;
	href?: string | null;
	isPrimary?: boolean;
	primaryLabel?: string;
	isVerified?: boolean;
	verifiedText?: string | null;
	note?: string | null;
	className?: string;
}

const compact = (value?: string | null) => {
	const normalized = value?.trim();
	return normalized ? normalized : null;
};

const resolveContactIcon = (value?: string | null) => {
	const normalized = value?.toLowerCase() ?? '';

	if (
		normalized.includes('mobile') ||
		normalized.includes('cell') ||
		normalized.includes('sms')
	) {
		return Smartphone;
	}

	if (
		normalized.includes('phone') ||
		normalized.includes('telephone') ||
		normalized.includes('tel') ||
		normalized.includes('home') ||
		normalized.includes('landline')
	) {
		return Phone;
	}

	if (normalized.includes('mail') || normalized.includes('email')) {
		return Mail;
	}

	if (
		normalized.includes('whatsapp') ||
		normalized.includes('viber') ||
		normalized.includes('telegram') ||
		normalized.includes('chat') ||
		normalized.includes('message')
	) {
		return MessageCircle;
	}

	if (
		normalized.includes('website') ||
		normalized.includes('web') ||
		normalized.includes('url') ||
		normalized.includes('site')
	) {
		return Globe;
	}

	if (
		normalized.includes('address') ||
		normalized.includes('location')
	) {
		return MapPin;
	}

	return User;
};

export function CustomerContactDisplayItem({
	label,
	iconHint,
	value,
	href,
	isPrimary,
	primaryLabel = 'Primary',
	isVerified,
	verifiedText,
	note,
	className
}: CustomerContactDisplayItemProps) {
	const resolvedLabel = compact(label) ?? 'Contact';
	const resolvedValue = compact(value);
	const noteText = compact(note);
	const verifiedBadgeText = compact(verifiedText);
	const ContactIcon = resolveContactIcon(iconHint ?? resolvedLabel);

	return (
		<div className={cn('bg-transparent', className)}>
			<div className="flex items-center gap-3">
				<Tooltip>
					<TooltipTrigger asChild>
						<span
							className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-transparent text-gray-400 dark:border-gray-600 dark:text-gray-400"
							title={resolvedLabel}
							aria-label={resolvedLabel}
						>
							<ContactIcon className="h-4 w-4" />
						</span>
					</TooltipTrigger>
					<TooltipContent side="top" sideOffset={6}>
						{resolvedLabel}
					</TooltipContent>
				</Tooltip>
				<div className="min-w-0 flex flex-1 items-center justify-between gap-3">
					<div className="min-w-0 flex items-center gap-3">
						<span className="sr-only">{resolvedLabel}</span>
						{resolvedValue ? (
							href ? (
								<a
									href={href}
									className="truncate whitespace-nowrap text-sm font-medium text-primary hover:underline"
								>
									{resolvedValue}
								</a>
							) : (
								<span className="truncate whitespace-nowrap text-sm font-medium text-foreground">
									{resolvedValue}
								</span>
							)
						) : (
							<span className="text-sm text-muted-foreground">-</span>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-2">
							{isPrimary ? (
								<Badge variant="secondary">{primaryLabel}</Badge>
							) : null}
							{isVerified && verifiedBadgeText ? (
								<Badge variant="outline">{verifiedBadgeText}</Badge>
							) : null}
					</div>
				</div>
			</div>
			{noteText ? (
				<p className="mt-1 pl-11 text-sm text-muted-foreground">{noteText}</p>
			) : null}
		</div>
	);
}
